import {
  Autocomplete,
  Box,
  Card,
  // Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  // FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
// import { useDebounce } from "../../../helpers";
import { useSnackbar } from "notistack";
import { Add, Create } from "@mui/icons-material";
import ClassCreator from "./ClassCreator";
import { myClassesServices } from "../../../services";
import { useSelector } from "react-redux";
import { initialClassState } from "./classInitialisation";

const PREFIX = "ClassManagement";

const classes = {
  root: `${PREFIX}-root`,
  marginBottom: `${PREFIX}-marginBottom`,
  autocomplete: `${PREFIX}-autocomplete`,
  addClassButton: `${PREFIX}-addClassButton`,
  iconColor: `${PREFIX}-iconColor`,
  classesListMainContainer: `${PREFIX}-classesListMainContainer`,
  classesListElementContainer: `${PREFIX}-classesListElementContainer`,
  classesListElementTitle: `${PREFIX}-classesListElementTitle`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.root}`]: {
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    maxWidth: "650px",
    width: "100%",
    minWidth: "400px",
  },
  [`& .${classes.marginBottom}`]: {
    marginBottom: "1rem",
  },
  [`& .${classes.autocomplete}`]: {
    width: "100%",
  },
  [`& .${classes.addClassButton}`]: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: "50%",
    height: "45px",
    width: "45px",
    marginLeft: "1rem",
    "&:hover": {
      backgroundColor: "rgba(147, 122, 66)",
    },
  },
  [`& .${classes.iconColor}`]: {
    color: "#000",
  },

  [`& .${classes.classesListMainContainer}`]: {
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    maxHeight: "400px",
    padding: "0.5rem",
    marginTop: "1rem",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#d3af5f",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
  },
  [`& .${classes.classesListElementContainer}`]: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1rem",
    marginBottom: "0.5rem",
    minHeight: "80px",
  },
  [`& .${classes.classesListElementTitle}`]: {
    maxWidth: "160px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

const classDurations = [30, 60, 90, 120, 150, 180];

const initialClassManagement = { open: false, selectedChip: null };

const SecurityClassManager = ({ isAdmin, exparience, setExparience }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  // const [isClass, setIsClass] = useState(exparience.groups.length > 0);
  const [fetching, setFetching] = useState({
    groups: false,
    input: false,
  });
  const [groups, setGroups] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [classManagement, setClassManagement] = useState(
    initialClassManagement
  );

  const accountId = useSelector((state) => state.user.currentUser.account.id);

  const fetchGroups = useCallback(
    (groupIds = []) => {
      myClassesServices
        .getGroups(isAdmin, groupIds)
        .then((res) => {
          let tmp = [];
          if (isAdmin) {
            tmp = res.map((item) => ({
              groupId: item.id,
              details: { ...item },
            }));
          } else {
            tmp = res
              // .filter((item) => item.group.createdBy.account_id === accountId)
              .map((item) => ({
                groupId: item.group.id,
                details: { ...item.group },
                membersCount: item.membersCount,
              }));
          }
          setGroups(tmp);
          setFetching({ ...fetching, groups: false });
        })
        .catch((err) => {
          console.log("error =>", err);
          enqueueSnackbar(t("toasts.groupsError"), { variant: "error" });
        });
    },
    [enqueueSnackbar, t, fetching, isAdmin]
  );

  useEffect(() => {
    if (!groups) {
      fetchGroups(exparience.groups.map((item) => item.groupId));
    }
  }, [groups, fetchGroups, exparience.groups]);

  // const debouncedInput = useDebounce(inputValue, 500);

  // useEffect(() => {
  //   if (debouncedInput.length > 0) {
  //     setFetching({...fetching, input: true});
  //     userService
  //       .searchUser(debouncedInput)
  //       .then((res) => {
  //         setFetchedUsers(res);
  //       })
  //       .catch((err) => {
  //         if (err === 403)
  //           enqueueSnackbar(t("toasts.unauthorizedError"), {
  //             variant: "error",
  //           });
  //         else enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
  //       })
  //       .finally(() => setFetching({...fetching, input: false});;
  //   }
  // }, [debouncedInput, enqueueSnackbar, t]);

  const addGroupToExparienceAfterCreation = (
    data,
    isModification,
    newGroup
  ) => {
    const tmp = [...exparience.groups];
    const tmpGroups = [...groups];
    if (!isModification) {
      tmp.push(data);
      tmpGroups.push({ groupId: data.groupId, details: newGroup });
    } else {
      const index = tmp.findIndex((item) => item.id === data.id);
      tmp[index] = { ...data, duration: tmp[index].duration };
      const groupIndex = tmpGroups.findIndex(
        (elem) => elem.groupId === newGroup.id
      );
      tmpGroups[groupIndex] = { ...tmpGroups[groupIndex], details: newGroup };
    }
    setGroups(tmpGroups);
    setExparience((prev) => ({ ...prev, groups: tmp }));
  };

  const exparienceGroups = useMemo(
    () =>
      exparience.groups.filter((item) =>
        groups?.some((elem) => elem.groupId === item.groupId)
      ),
    [exparience.groups, groups]
  );

  return (
    <Root>
      <ClassCreator
        open={classManagement.open}
        selectedClass={classManagement.selectedChip}
        // isModification={!!classManagement.selectedChip?.id}
        onClose={() => setClassManagement(initialClassManagement)}
        isAdmin={isAdmin}
        expId={exparience.id}
        addGroupToExparienceAfterCreation={addGroupToExparienceAfterCreation}
      />
      <Box className={classes.root}>
        {/* <FormControlLabel
          control={
            <Checkbox
              color={"primary"}
              onChange={(e) => setIsClass(e.target.checked)}
              checked={isClass || false}
            />
          }
          label={
            <Typography variant="body1">
              {t("myExpariences.class.classSecurityTitle")}
            </Typography>
          }
          className={classes.marginBottom}
        /> */}
        {!groups ? (
          <CircularProgress size={50} />
        ) : (
          // isClass &&
          groups && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                marginBottom: "1rem",
              }}
            >
              <Box
                sx={{ display: "flex", width: "100%", alignItems: "center" }}
              >
                <Autocomplete
                  label="classes"
                  id="autocomplete-classes"
                  disableClearable
                  multiple
                  disabled={fetching.groups}
                  className={classes.autocomplete}
                  loading={fetching.groups || fetching.input}
                  isOptionEqualToValue={(option, value) =>
                    option.groupId === value.groupId
                  }
                  options={groups || []}
                  filterOptions={(fields) =>
                    fields.filter(
                      (item) => item.details.createdBy.account_id === accountId
                    )
                  }
                  getOptionLabel={(option) => option.details.name}
                  value={
                    exparience.groups.map((group) => {
                      return {
                        ...groups?.find(
                          (item) => item.groupId === group.groupId
                        ),
                      };
                    }) || []
                  }
                  onChange={(e, value) => {
                    setExparience({
                      ...exparience,
                      groups: value.map((item) => ({
                        ...item,
                        duration:
                          exparience.groups.find(
                            (elem) => elem.groupId === item.groupId
                          )?.duration || 30,
                      })),
                    });
                  }}
                  inputValue={inputValue}
                  renderTags={(tagValue, getTagProps) => {
                    return tagValue.map((option, index) => {
                      const isNotOwner = isAdmin
                        ? false
                        : option.details.createdBy.account_id !== accountId;
                      if (Object.keys(option).length === 0) return null;
                      else
                        return (
                          <Chip
                            {...getTagProps({ index })}
                            label={`${option.details?.name}`}
                            disabled={isNotOwner}
                            icon={
                              <Create
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setClassManagement({
                                    open: true,
                                    selectedChip: {
                                      ...option.details,
                                    },
                                  });
                                }}
                                sx={{
                                  color: "#d3Af5f !important",
                                  width: "25px",
                                  height: "25px",
                                  cursor: "pointer",
                                  borderRadius: "10px",
                                  "&:hover": {
                                    backgroundColor: "#d3af5f",
                                    fill: "#515151",
                                  },
                                }}
                              />
                            }
                          />
                        );
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("myExpariences.class.searchClass")}
                      onChange={(e) => setInputValue(e.target.value)}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {fetching.input || fetching.groups ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />

                <IconButton
                  color="primary"
                  component="span"
                  className={classes.addClassButton}
                  size="large"
                  onClick={() =>
                    setClassManagement({
                      open: true,
                      selectedChip: initialClassState,
                    })
                  }
                >
                  <Add className={classes.iconColor} />
                </IconButton>
              </Box>
              <Box>
                {exparienceGroups.length !== 0 && (
                  <Card
                    elevation={12}
                    className={classes.classesListMainContainer}
                  >
                    {exparienceGroups.map((item, index) => {
                      const itemFromGroup = groups.find(
                        (elem) => elem.groupId === item.groupId
                      );
                      const currentGroup = groups.find(
                        (element) => element.details.id === item.groupId
                      );
                      const isOwner =
                        isAdmin ||
                        currentGroup.details.createdBy.account_id === accountId;
                      return (
                        <Card
                          key={`exparience-group-${item.groupId}-${index + 1}`}
                          elevation={8}
                          className={classes.classesListElementContainer}
                        >
                          <Typography
                            variant="body2"
                            className={classes.classesListElementTitle}
                          >
                            {itemFromGroup?.details?.name || "???"}
                          </Typography>
                          <FormControl fullWidth sx={{ width: "50%" }}>
                            <InputLabel id="class-time-access">
                              {t("myExpariences.class.limitedAccessTime")}
                            </InputLabel>
                            <Select
                              labelId="class-time-access"
                              id="demo-simple-select"
                              value={item.duration}
                              label={t("myExpariences.class.limitedAccessTime")}
                              disabled={!isOwner}
                              onChange={(e) => {
                                const tmp = [...exparience.groups];
                                tmp[index] = {
                                  ...tmp[index],
                                  duration: e.target.value,
                                };
                                setExparience((prev) => ({
                                  ...prev,
                                  groups: tmp,
                                }));
                              }}
                            >
                              {classDurations.map((duration) => (
                                <MenuItem value={duration} key={duration}>
                                  {duration}{" "}
                                  {t("myExpariences.class.minutesSelect")}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Card>
                      );
                    })}
                  </Card>
                )}
              </Box>
            </Box>
          )
        )}
      </Box>
    </Root>
  );
};

export default SecurityClassManager;
